/* AURYN MOTION SYSTEM — 20260919 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const heroToken = (el) =>
    Array.from(el.classList || []).some((token) => token === 'hero' || token.endsWith('-hero'));

  const markVisible = (el) => el.classList.add('auryn-motion-visible');

  let revealObserver = null;
  let heroObserver = null;

  const ensureObservers = () => {
    if (reduceMotion.matches || revealObserver) return;

    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        requestAnimationFrame(() => markVisible(entry.target));
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -6% 0px'
    });

    heroObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('auryn-motion-hero-visible');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -2% 0px'
    });
  };

  const prepareReveal = (el, delay = 0, kind = 'soft') => {
    if (!el || el.dataset.aurynMotionBound === 'true') return;
    el.dataset.aurynMotionBound = 'true';
    el.classList.add('auryn-motion-reveal');
    el.dataset.aurynMotionKind = kind;
    el.style.setProperty('--auryn-motion-delay', `${Math.min(delay, 260)}ms`);

    if (reduceMotion.matches) {
      markVisible(el);
      return;
    }

    ensureObservers();
    revealObserver.observe(el);
  };

  const prepareHero = (hero) => {
    if (!hero || hero.dataset.aurynHeroMotionBound === 'true') return;
    hero.dataset.aurynHeroMotionBound = 'true';
    hero.classList.add('auryn-motion-hero');

    const alreadyAnimated =
      hero.classList.contains('auryn-campaign-hero--motion') ||
      hero.closest('.auryn-campaign-hero--motion');

    const image = hero.querySelector(
      '.auryn-collection-hero__image,' +
      '.auryn-campaign-hero__image,' +
      '.auryn-gender-hero__image,' +
      '.ab-hero-media img,' +
      '.s-hero-media img,' +
      'img'
    );

    if (image && !alreadyAnimated) {
      image.classList.add('auryn-motion-hero-image');
    }

    const copySelectors = [
      '[class*="kicker"]',
      '[class*="eyebrow"]',
      'h1',
      '[class*="tagline"]',
      '[class*="lead"]',
      '[class*="hero-sub"]',
      '[class$="__description"]',
      '[class$="__cta"]',
      '.ac-hero-copy > p:not(.ac-kicker)',
      '.ac-text-link',
      '.cp-intro',
      '.cp-meta',
      '.hv3-actions',
      '.hv3-script',
      '.ab-hero-sign',
      '.s-hero-index'
    ].join(',');

    const copyItems = Array.from(hero.querySelectorAll(copySelectors))
      .filter((el) => !el.closest('nav'))
      .filter((el, index, arr) => arr.indexOf(el) === index);

    copyItems.forEach((el, index) => prepareReveal(el, 80 + index * 70, 'soft'));

    if (reduceMotion.matches) {
      hero.classList.add('auryn-motion-hero-visible');
      return;
    }

    ensureObservers();
    heroObserver.observe(hero);
  };

  const setupCustomRoot = (root) => {
    if (!root || root.classList.contains('auryn-cookie')) return;
    root.classList.add('auryn-motion-root');

    const heroCandidates = [root, ...root.querySelectorAll('header,section,div')]
      .filter(heroToken);

    heroCandidates.forEach(prepareHero);

    const candidateSelector = [
      'h2',
      'h3',
      'blockquote',
      '[class*="card"]',
      '[class*="value"]',
      '[class*="feature"]',
      '[class*="media"]',
      '[class*="quote"]',
      '[class*="manifesto"]',
      '[class*="chapter"]',
      '[class*="panel"]',
      '[class*="note"]',
      '[class*="intro"]',
      '[class*="cta"]'
    ].join(',');

    const candidates = Array.from(root.querySelectorAll(candidateSelector));

    let sequence = 0;
    candidates.forEach((el) => {
      if (el.closest('.auryn-motion-hero')) return;
      if (el.closest('.auryn-motion-reveal')) return;
      if (el.matches('script,style,noscript')) return;

      const className = typeof el.className === 'string' ? el.className : '';
      const isCard = /(card|value|feature|panel)/i.test(className);
      const isMedia = /(media|image|picture)/i.test(className);

      if (isCard) el.classList.add('auryn-motion-card');

      prepareReveal(
        el,
        (sequence % 4) * 65,
        isMedia ? 'media' : 'soft'
      );

      sequence += 1;
    });
  };

  const setupStandardStorefront = (scope = document) => {
    const gridItems = scope.querySelectorAll(
      '.product-grid > .grid__item:not(.scroll-trigger),' +
      '.blog-articles > *:not(.scroll-trigger),' +
      '.collection-list .grid__item:not(.scroll-trigger)'
    );

    gridItems.forEach((el, index) => {
      if (el.dataset.aurynGridMotionBound === 'true') return;
      el.dataset.aurynGridMotionBound = 'true';
      el.classList.add('auryn-motion-grid-item');
      el.style.setProperty('--auryn-motion-delay', `${(index % 4) * 65}ms`);

      if (reduceMotion.matches) {
        markVisible(el);
      } else {
        ensureObservers();
        revealObserver.observe(el);
      }
    });

    const firstProductImage = scope.querySelector('.product__media-item:first-child img');
    if (firstProductImage && firstProductImage.dataset.aurynProductMotionBound !== 'true') {
      firstProductImage.dataset.aurynProductMotionBound = 'true';
      firstProductImage.classList.add('auryn-motion-product-image');

      if (reduceMotion.matches) {
        markVisible(firstProductImage);
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => markVisible(firstProductImage));
        });
      }
    }

    const main = scope.querySelector?.('main') || (scope.matches?.('main') ? scope : null);
    if (main) {
      const firstShopifySection = main.querySelector('.shopify-section');
      const nativeHero = firstShopifySection?.querySelector('.banner, slideshow-component');
      if (nativeHero && nativeHero.dataset.aurynNativeHeroBound !== 'true') {
        nativeHero.dataset.aurynNativeHeroBound = 'true';
        nativeHero.classList.add('auryn-motion-root');

        const nativeImage = nativeHero.querySelector('img');
        if (nativeImage) nativeImage.classList.add('auryn-motion-hero-image');

        const nativeCopy = nativeHero.querySelectorAll(
          'h1, .banner__heading, .banner__text, .banner__buttons'
        );
        nativeCopy.forEach((el, index) => prepareReveal(el, 80 + index * 75, 'soft'));

        nativeHero.classList.add('auryn-motion-hero');
        if (reduceMotion.matches) {
          nativeHero.classList.add('auryn-motion-hero-visible');
        } else {
          ensureObservers();
          heroObserver.observe(nativeHero);
        }
      }
    }
  };

  const init = (scope = document) => {
    const roots = [];

    if (scope.matches?.('section[id^="Auryn"], div[id^="Auryn"]')) roots.push(scope);

    scope.querySelectorAll?.(
      'main section[id^="Auryn"], main div[id^="Auryn"]'
    ).forEach((el) => roots.push(el));

    roots
      .filter((el, index, arr) => arr.indexOf(el) === index)
      .filter((el) => el.id !== 'AurynCookieConsent')
      .forEach(setupCustomRoot);

    setupStandardStorefront(scope);
  };

  const boot = () => {
    init(document);
    document.documentElement.classList.add('auryn-motion-initialized');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init(document));

  reduceMotion.addEventListener?.('change', () => {
    document
      .querySelectorAll('.auryn-motion-reveal,.auryn-motion-grid-item,.auryn-motion-product-image')
      .forEach(markVisible);

    document
      .querySelectorAll('.auryn-motion-hero')
      .forEach((el) => el.classList.add('auryn-motion-hero-visible'));
  });
})();
